import {
  createContext,
  Dispatch,
  ReactNode,
  useContext,
  useState,
} from "react";
import { Condition } from "~/types";

// context type
type SearchContextType = {
  conditions: Condition;
  appliedConditions: Condition;
  addCondition: (type: keyof Condition, value: string | string[]) => void;
  removeCondition: (type: keyof Condition, value: string | string[]) => void;
  resetConditions: () => void;
  setConditions: Dispatch<React.SetStateAction<Condition>>;
  setAppliedConditions: Dispatch<React.SetStateAction<Condition>>;
  initialState: Condition;
};

// Initial state
export const initialState: Condition = {
  occupation_type_ids: [],
  search_area_ids: [],
  service_type_ids: [],
};

// Create context
const SearchContext = createContext<SearchContextType | undefined>(undefined);

// Provider component
export function SearchProvider({
  children,
  initialConditions = initialState,
}: {
  children: ReactNode;
  initialConditions?: Condition;
}) {
  const [conditions, setConditions] = useState<Condition>(initialConditions);
  const [appliedConditions, setAppliedConditions] =
    useState<Condition>(initialConditions);

  const addCondition = (type: keyof Condition, value: string | string[]) => {
    setConditions((prev) => {
      const newValue = Array.isArray(value) ? value : [value];
      const updateValue = newValue.filter((id) => !prev[type].includes(id));
      return {
        ...prev,
        [type]: [...prev[type], ...updateValue],
      };
    });
  };

  const removeCondition = (type: keyof Condition, value: string | string[]) => {
    setConditions((prev) => {
      const newValue = Array.isArray(value) ? value : [value];
      return {
        ...prev,
        [type]: [...prev[type].filter((id) => !newValue.includes(id))],
      };
    });
  };

  const resetConditions = () => {
    setConditions(initialState);
  };
  const value = {
    conditions,
    appliedConditions,
    addCondition,
    removeCondition,
    resetConditions,
    setConditions,
    setAppliedConditions,
    initialState,
  };
  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
}

export function useSearch() {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
}
